import {UAParser} from 'ua-parser-js';

class DetectBrowser {
  constructor() {
    this.eventPrefix = 'browser';
    this.debug = false; // Set to true to enable debug logging.

    // Initialize UA Parser
    this.parser = new UAParser();
    this.result = this.parser.getResult();

    this.initialized = false;
    // Extract info using ua-parser-js
    this.userAgent = this.result.ua;
    this.browser = this.getBrowserInfo();
    this.os = null; // Will be set async
    this.device = this.getDeviceType();
    this.orientation = this.getOrientation();
    this.classList = [];
    this.outdated = false;
  }

  /**
   * Initialize OS detection
   */
  initOS() {
    if (!this.initialized) {
      this.os = this.getOSInfo();
      this.log( 'OS detected:', this.os );
      this.initialized = true;
    }

    this.outdated = this.isSafari() && this.isSafariOutdatedSafari();
    this.storeBrowserInfo();
  }

  /**
   * Setup custom event listeners.
   */
  setupEventListeners() {
    window.addEventListener( `${this.eventPrefix}:resize`, () => this.resize() );
    window.addEventListener( `${this.eventPrefix}:orientationchange`, () => this.updateOrientation() );
    window.addEventListener( `${this.eventPrefix}:setBodyClasses`, () => this.setBodyClasses() );

    window.addEventListener( `${this.eventPrefix}:toggleDebug`, () => {
      this.debug = !this.debug;
      this.log( 'Debug mode toggled:', this.debug );
    } );

    window.addEventListener( `${this.eventPrefix}:enableDebug`, () => {
      this.debug = true;
      this.log( 'Debug mode enabled' );
    } );

    window.addEventListener( `${this.eventPrefix}:disableDebug`, () => {
      this.debug = false;
      this.log( 'Debug mode disabled' );
    } );

    window.addEventListener( `${this.eventPrefix}:refresh`, () => {
      this.log( 'Refreshing detection...' );
      this.initialized = false; // Otherwise OS won't refresh.
      this.refresh();
    } );

    window.addEventListener( `${this.eventPrefix}:logInfo`, () => this.logInfo() );

    // Initial setup.
    this.initOS();
    this.dispatchEvent( 'setBodyClasses' );
  }

  /**
   * Dispatch a custom event with optional data.
   *
   * @param {string} name The name of the event to dispatch.
   * @param {Object} data Optional data to include with the event.
   */
  dispatchEvent(name, data = {}) {
    if (!name) {
      return;
    }

    this.debug && console.group( `Dispatching event: ${name}` );
    this.debug && console.info( data );

    // Split name on `:` to add prefix if missing. (internal or external event).
    if (name.indexOf( ':' ) === -1) {
      this.debug && console.log( `Adding event prefix: ${this.eventPrefix}` );
      name = `${this.eventPrefix}:${name}`;
    }

    window.dispatchEvent( new CustomEvent( name, {
      detail: data,
      bubbles: true
    } ) );

    this.debug && console.groupEnd();
  }

  /**
   * Log message if debug mode is enabled.
   * @param {...any} args - Arguments to log
   */
  log(...args) {
    if (this.debug) {
      console.log( '[DetectBrowser]:', ...args );
    }
  }

  /**
   * Log current detection info.
   */
  logInfo() {
    console.group( '[DetectBrowser]: Browser Detection Info' );
    console.log( 'Browser:', this.browser );
    console.log( 'Browser Version:', this.result.browser.version );
    console.log( 'OS:', this.os );
    console.log( 'OS Version (from UA):', this.result.os.version );
    console.log( 'Device:', this.device );
    console.log( 'Device Type:', this.result.device.type || 'desktop' );
    console.log( 'Device Vendor:', this.result.device.vendor || 'N/A' );
    console.log( 'Device Model:', this.result.device.model || 'N/A' );
    console.log( 'Orientation:', this.orientation );
    console.log( 'CPU Architecture:', this.result.cpu.architecture || 'N/A' );
    console.log( 'Engine:', this.result.engine.name || 'N/A' );
    console.log( 'User Agent:', this.userAgent );
    console.log( 'Debug Mode:', this.debug );
    console.log( 'Full Parser Result:', this.result );
    console.log( 'ClassList:', this.classList );
    console.groupEnd();
  }

  /**
   * Refresh all detection values.
   */
  refresh() {
    // Clear cached info to force re-detection
    window.sessionStorage.removeItem( 'nl-design-system-theme-browser-detector-os-info' );
    window.sessionStorage.removeItem( 'nl-design-system-theme-browser-info' );
    window.sessionStorage.removeItem( 'nl-design-system-theme-browser-outdated' );

    this.parser.setUA( navigator.userAgent );
    this.result = this.parser.getResult();
    this.userAgent = this.result.ua;
    this.browser = this.getBrowserInfo();
    this.os = this.getOSInfo();
    this.device = this.getDeviceType();
    this.orientation = this.getOrientation();

    this.log( 'Detection refreshed' );

    this.debug && this.dispatchEvent( 'refreshed', {
      browser: this.browser,
      os: this.os,
      device: this.device,
      orientation: this.orientation
    } );
  }

  /**
   * Check if browser is Chrome.
   * @return {boolean}
   */
  isChrome() {
    return this.result.browser.name === 'Chrome' || this.result.browser.name === 'Chromium';
  }

  /**
   * Check if browser is Firefox.
   * @return {boolean}
   */
  isFirefox() {
    return this.result.browser.name === 'Firefox';
  }

  /**
   * Check if browser is Safari.
   * @return {boolean}
   */
  isSafari() {
    return this.result.browser.name === 'Safari' || this.result.browser.name === 'Mobile Safari';
  }

  /**
   * Check if browser is Edge.
   * @return {boolean}
   */
  isEdge() {
    return this.result.browser.name === 'Edge';
  }

  /**
   * Check if browser is Opera.
   * @return {boolean}
   */
  isOpera() {
    return this.result.browser.name === 'Opera';
  }

  /**
   * Get browser information.
   * @return {string}
   */
  getBrowserInfo() {
    const browserName = this.result.browser.name || 'Unknown';
    // Normalize browser name
    return browserName.replace( /\s+/g, '-' );
  }

  /**
   * Store browser information and outdated status in sessionStorage.
   */
  storeBrowserInfo() {
    const browserCacheKey = 'nl-design-system-theme-browser-info';
    const outdatedCacheKey = 'nl-design-system-theme-browser-outdated';

    window.sessionStorage.setItem( browserCacheKey, this.browser );
    window.sessionStorage.setItem( outdatedCacheKey, this.outdated ? '1' : '0' );

    this.log( 'Browser info stored in sessionStorage:', {
      browser: this.browser,
      outdated: this.outdated
    } );
  }

  /**
   * Get OS information.
   * @return {string}
   */
  getOSInfo() {
    // Check sessionStorage first for cached OS info
    const cacheKey = 'nl-design-system-theme-browser-detector-os-info';
    const cachedOS = window.sessionStorage.getItem( cacheKey );

    if (cachedOS) {
      this.log( 'Using cached OS info:', cachedOS );
      return cachedOS;
    }

    // Calculate OS info
    const osName = this.result.os.name || 'Unknown';
    let osVersion = this.result.os.version || '';

    // Clean up OS name, replace spaces with `-`.
    let cleanName = osName.replace( /\s+/g, '-' );

    let detectedOS;

    // Handle macOS detection (case-insensitive)
    if (/^mac(-?os)?(-?x)?$/i.test( cleanName )) {
      detectedOS = 'Mac';
    } else {
      switch (cleanName) {
        case 'Windows':
          if (osVersion === '10') detectedOS = 'Windows-10';
          else if (osVersion === '11') detectedOS = 'Windows-11';
          else if (osVersion) {
            const majorVersion = osVersion.split( '.' )[0];
            detectedOS = `Windows-${majorVersion}`;
          } else {
            detectedOS = 'Windows';
          }
          break;

        case 'Android':
        case 'iOS':
          if (osVersion) {
            const majorVersion = osVersion.split( '.' )[0];
            detectedOS = `${cleanName}-${majorVersion}`;
          } else {
            detectedOS = cleanName;
          }
          break;
        default:
          detectedOS = cleanName;
      }
    }

    // Store in sessionStorage..
    window.sessionStorage.setItem( cacheKey, detectedOS );
    this.log( 'OS info detected and cached:', detectedOS );

    return detectedOS;
  }

  /**
   * Check if Safari is version 17 or older using feature detection.
   *
   * @return {boolean}
   */
  isSafariOutdatedSafari() {
    return !(('startViewTransition' in document)
      || CSS.supports( 'view-transition-name', 'foo' ))
  }

  /**
   * Get device type.
   * @return {string}
   */
  getDeviceType() {
    const deviceType = this.result.device.type;
    const osName = this.result.os.name;

    // If ua-parser-js detected a device type
    if (deviceType) {
      if (deviceType === 'mobile') {
        if (osName === 'iOS') return 'iPhone';
        if (osName === 'Android') return 'Android-Mobile';
        return 'Mobile';
      }

      if (deviceType === 'tablet') {
        if (osName === 'iOS') return 'iPad';
        if (osName === 'Android') return 'Android-Tablet';
        return 'Tablet';
      }

      // Return other device types (console, smart-tv, wearable, embedded).
      return deviceType.charAt( 0 ).toUpperCase() + deviceType.slice( 1 );
    }

    // Fallback to manual detection.
    if (/iPhone/.test( this.userAgent )) return 'iPhone';
    if (/iPad/.test( this.userAgent )) return 'iPad';
    if (/iPod/.test( this.userAgent )) return 'iPod';

    return 'Desktop';
  }

  /**
   * Get current orientation.
   * @return {string}
   */
  getOrientation() {
    return window.matchMedia( '(orientation: portrait)' ).matches ? 'portrait' : 'landscape';
  }

  resize() {
    this.dispatchEvent( 'updateOrientation' );
  }

  /**
   * Update orientation class on body.
   */
  updateOrientation() {
    // Get className starting with 'orientation-' from classList of the body and remove them.
    this.removeBodyClasses( Array.from( document.body.classList )?.filter( className => className.startsWith( 'orientation-' ) ), false );

    // Remove existing orientation classes from classList.
    this.classList = this.classList?.filter( className => !className.startsWith( 'orientation-' ) );

    // Update all classes again.
    this.dispatchEvent( 'setBodyClasses' );
  }

  /**
   * Set all CSS classes on the body element.
   */
  setBodyClasses() {
    // Ensure OS is detected
    if (!this.os) {
      this.os = this.getOSInfo();
    }

    const newClasses = [
      `browser-${this.browser.toLowerCase()}`,
      `os-${this.os.toLowerCase()}`,
      `device-${this.device.toLowerCase()}`,
      `orientation-${this.getOrientation().toLowerCase()}`
    ];

    if (this.outdated) {
      newClasses.push( 'browser-outdated' );
    }

    this.enqueueBrowserSpecificStyle();

    // Only add classes that aren't already in the list
    newClasses.forEach( className => this.classList.push( className ) );
    this.classList?.forEach( (className) => document.body.classList.add( className ) );

    this.log( 'Body classes set:', this.classList );
  }

  /**
   * Remove all detection classes from body.
   */
  removeBodyClasses(classList = [], flush = true) {
    if (!classList.length && flush) {
      classList = this.classList || [];
      this.classList = [];
    }

    classList.forEach( (className) => document.body.classList.remove( className ) );

    this.log( 'Body classes removed:', classList );
  }

  enqueueBrowserSpecificStyle() {
    if (!this.browser || this.browser === 'Unknown') {
      return;
    }

    // @todo: Extend this to other browsers if needed.
    if (!this.isSafari() || !this.isSafariOutdatedSafari()) {
      return;
    }

    try {
      const fileName = this.outdated ? `browser-${this.browser.toLowerCase()}-outdated` : `browser-${this.browser.toLowerCase()}`;
      const linkId = `nl-design-system-theme-${fileName}`;

      // Check if already enqueued
      if (document.getElementById( linkId )) {
        this.log( `Style already enqueued for ${this.browser}${this.outdated ? ' (outdated)' : ''}` );
        return;
      }

      // Check sessionStorage first to prevent multiple calls.
      const cachedStyle = !this.debug && window.sessionStorage.getItem( `${linkId}-style` );
      const cachedScript = !this.debug && window.sessionStorage.getItem( `${linkId}-script` );

      if (cachedStyle || cachedScript) {
        // File URL is cached, use it directly.
        cachedStyle && this.appendStylesheet( `${linkId}-style`, cachedStyle );
        cachedScript && this.appendScript( `${linkId}-script`, cachedScript );
        this.log( `Enqueued cached assets for ${this.browser}${this.outdated ? ' (outdated)' : ''}` );
        return;
      }

      const {ajax_url, nonce} = window.wp_variables || {};

      // Make AJAX call to check if file exists before enqueuing
      fetch( ajax_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams( {
          action: 'get_browser_specific_assets',
          nonce: nonce,
          browser: this.browser,
          outdated: this.outdated ? 1 : 0
        } )
      } )
        .then( response => {
          if (!response.ok) {
            throw new Error( `HTTP error! status: ${response.status}` );
          }
          return response.json(); // Return the promise to chain it
        } )
        .then( data => {

          // Now 'data' contains the parsed JSON response
          const {data: responseData} = data;
          const {script, style} = responseData || {};

          if (style) {
            // On success: Store file URI in sessionStorage to prevent multiple calls
            window.sessionStorage.setItem( `${linkId}-style`, style );

            // Enqueue the style
            this.appendStylesheet( `${linkId}-style`, style );
            this.log( `Enqueued style for ${this.browser}${this.outdated ? ' (outdated)' : ''}: ${style}` );
          }

          if (script) {
            // On success: Store file URI in sessionStorage to prevent multiple calls
            window.sessionStorage.setItem( `${linkId}-script`, script );

            // Enqueue the script
            this.appendScript( `${linkId}-script`, script );
            this.log( `Enqueued script for ${this.browser}${this.outdated ? ' (outdated)' : ''}: ${script}` );
          }

          if (!style && !script) {
            this.log( `No assets found for ${this.browser}${this.outdated ? ' (outdated)' : ''}` );
          }
        } )
        .catch( error => this.log( `Error checking style for ${this.browser}${this.outdated ? ' (outdated)' : ''}:`, error ) );
    } catch (error) {
      this.log( `Error in enqueueBrowserSpecificStyle for ${this.browser}${this.outdated ? ' (outdated)' : ''}:`, error );
    }
  }

  /**
   * Append stylesheet to document head.
   * @param {string} id - The ID for the link element.
   * @param {string} url - The URL of the stylesheet.
   */
  appendStylesheet(id, url) {
    if (!url.length) {
      return;
    }

    const link = document.createElement( 'link' );
    link.rel = 'stylesheet';
    link.id = id;
    link.href = url;
    document.head.appendChild( link );
  }

  /**
   * Append script to document head.
   * @param {string} id The ID for the script element.
   * @param {string} url The URL of the script.
   */
  appendScript(id, url) {
    if (!url.length) {
      return;
    }

    const script = document.createElement( 'script' );
    script.type = 'text/javascript';
    script.id = id;
    script.src = url;
    document.head.appendChild( script );
  }
}

export default DetectBrowser;
